// @ts-ignore
import React from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";

import { LanguageSelector } from "../../../components/global-footer/language-selector/src";
import SocialLinks from "../../../components/global-footer/social-links/src";
import { LinkWithIcon } from "../../../components/global-nav/list-of-links/src";
import Link from "../../../components/wpl/link/src/index";

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css`
    border-top: 1px solid #dfe1e5;
    container-type: inline-size;
`;

const rowStyle = css`
    max-width: 1440px;
    margin-left: auto;
    margin-right: auto;
`;

const columnStyle = css`
    font-size: 0.875rem;

    display: flex;
    justify-content: space-between;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;

    ${UNSAFE_media.below.sm} {
        padding: 16px;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        row-gap: 5px;
    }

    ${UNSAFE_media.only.sm} {
        padding: 16px;
    }

    ${media.above.md} {
        margin-left: 8.33333%;
        width: 83.66666%;
    }
`;

const languageSelectorStyle = css`
    ${UNSAFE_media.below.sm} {
        margin-bottom: -5px;
    }
`;

const linkStyle = css`
    height: 26px;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
        text-shadow: none;
    }

    &:visited,
    &:focus {
        text-shadow: none;
    }

    ${UNSAFE_media.below.sm} {
        margin-left: 2px;
    }
`;

const copyrightStyle = css`
    color: #42526e;
    height: 26px;
    line-height: 26px;

    ${UNSAFE_media.below.sm} {
        margin-left: 2px;
    }
`;

const socialLinksStyle = css`
    align-self: flex-end;

    ${media.above.md} {
        margin-left: 20%;
    }
`;

// ==================================================================
// Type Props
// ==================================================================
export interface GlobalFooterBottomProps {
    selectedLanguage: string;
    onLanguageChangeHandler: React.Dispatch<string>;
    className?: string;
}

// ==================================================================
// Rendering Functions
// ==================================================================

export const GlobalFooterBottom = ({
    selectedLanguage,
    onLanguageChangeHandler,
    className
}: GlobalFooterBottomProps) => {
    const intl = useIntl();
    const bottomLinks = [
        {
            text: intl.formatMessage({
                id: "globalFooter.bottom.privacy-policy",
                defaultMessage: "Privacy policy"
            }),
            href: useLocalizedHref("/legal/privacy-policy"),
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "globalFooter.bottom.notice-at-collection",
                defaultMessage: "Notice at Collection"
            }),
            href: "/legal/privacy-policy#additional-disclosures-for-ca-residents",
            isOneTrustWrapped: true
        },
        {
            text: intl.formatMessage({
                id: "globalFooter.bottom.terms",
                defaultMessage: "Terms"
            }),
            href: "/legal/cloud-terms-of-service",
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "globalFooter.bottom.impressum",
                defaultMessage: "Impressum"
            }),
            href: "/legal/impressum",
            isOneTrustWrapped: false
        }
    ];

    return (
        <div className={className} css={containerStyle}>
            <div css={rowStyle}>
                <div css={columnStyle}>
                    <LanguageSelector
                        selectedLanguage={selectedLanguage}
                        onLanguageChangeHandler={onLanguageChangeHandler}
                        css={languageSelectorStyle}
                    />
                    {bottomLinks.map((bottomLink) => {
                        if (bottomLink.isOneTrustWrapped) {
                            return (
                                <div
                                    key={bottomLink.text}
                                    className={
                                        "link-wrapper atl-policy-link-container"
                                    }
                                >
                                    <LinkWithIcon
                                        linkText={bottomLink.text}
                                        href={bottomLink.href}
                                        type="anchor"
                                        isHaveIcon={false}
                                        isUsePlainSpanTagForLinkText={true}
                                        className={"atl-policy-link"}
                                        spanLinkTextClassName={
                                            "atl-policy-link-text"
                                        }
                                    />
                                </div>
                            );
                        } else {
                            return (
                                <Link
                                    key={bottomLink.text}
                                    text={bottomLink.text}
                                    href={bottomLink.href}
                                    type="anchor"
                                    customLinkColor={"n500"}
                                    css={linkStyle}
                                />
                            );
                        }
                    })}
                    <div css={copyrightStyle}>Copyright © 2023 Atlassian</div>
                    <SocialLinks css={socialLinksStyle} />
                </div>
            </div>
        </div>
    );
};
